import { PARITE } from './index.js';

/**
 * Passerelle entre le devis et le livre : que se passe-t-il, comptablement,
 * quand une cotation se réalise ?
 *
 * Elle vit ici et non dans le moteur de cotation ni dans le moteur de
 * position : le premier ignore le livre, le second ignore les devis, et ni
 * l'un ni l'autre ne doit apprendre le vocabulaire de l'autre. C'est le seul
 * endroit du code qui connaît les deux.
 *
 * Elle ne calcule aucun prix. Elle lit un résultat déjà produit par le moteur
 * de cotation et le répartit en mouvements.
 *
 * ── Un devis confirmé engendre PLUSIEURS mouvements ──────────────────────
 *
 * Un mouvement unique au taux effectif ferait entrer les frais dans le prix de
 * revient du stock. Sur 1 500 € en refacturé, le CMP tomberait à 641,90 au
 * lieu de 655,00 : les 30 € de frais de service auraient disparu dans le coût
 * du stock, d'où ils ne ressortiraient qu'à la revente, dilués sur tout le
 * pool. Le revenu serait différé et étalé, et toutes les marges réalisées
 * futures seraient fausses puisqu'elles se mesurent contre ce CMP.
 *
 * ── « out » veut dire ACHAT d'euros ──────────────────────────────────────
 *
 * Le sens du devis est nommé du point de vue du client, pas du nôtre. Un devis
 * `out` (EUR vers CFA) : le client européen nous remet des euros, nous
 * décaissons des CFA au pays. Nous finissons avec PLUS d'euros. C'est un
 * achat. Et `in` est une vente.
 */

/** Le sens de la cotation détermine le type du mouvement de change. */
export const TYPE_PAR_SENS = { out: 'achat_eur', in: 'vente_eur' };

/**
 * Décompose un devis confirmé en mouvements.
 *
 * Rend des brouillons : ni `id`, ni `date`, ni `quoteId`. La couche de
 * stockage les pose, ce module reste pur.
 */
export function mouvementsDuDevis({ resultat, rateOut, rateIn, corridor, parite = PARITE }) {
  if (!resultat) throw new Error('résultat de cotation manquant');
  if (corridor !== 'XAF' && corridor !== 'XOF') {
    throw new Error(`corridor attendu XAF ou XOF, reçu « ${corridor} »`);
  }

  return resultat.direction === 'out'
    ? sortant(resultat, rateOut, corridor, parite)
    : entrant(resultat, rateIn, corridor, parite);
}

/** Sens sortant : nous ACHETONS des euros. */
function sortant(r, rateOut, corridor, parite) {
  const mouvements = [];

  // 1. L'échange, au taux APPLIQUÉ. Seuls les euros réellement échangés
  //    entrent en stock : la part encaissée en frais est un revenu, pas de
  //    l'inventaire.
  const netEur = r.montantEnvoye - r.fraisEur;
  mouvements.push({
    type: 'achat_eur',
    corridor,
    montantEur: netEur,
    taux: rateOut,
    montantCfa: netEur * rateOut,
  });

  // 2. Les frais de service, encaissés en euros dans ce sens. `montantCfa`
  //    porte leur contribution au résultat, convertie à la parité et non au
  //    taux appliqué : au taux appliqué, le spread serait compté deux fois.
  if (r.fraisEur > 0) {
    mouvements.push({
      type: 'frais_service',
      corridor: 'EUR',
      montantEur: r.fraisEur,
      taux: 0,
      montantCfa: r.fraisEur * parite,
    });
  }

  // 3. Les frais opérateur.
  //
  //    En mode « déduit », ils ne sont PAS notre coût : ils sortent de
  //    l'enveloppe, le destinataire touche moins, et notre décaissement reste
  //    le brut. Émettre le mouvement entier déséquilibrerait la caisse de son
  //    montant exact.
  //
  //    Reste un résidu. La grille opérateur est un escalier - un plancher par
  //    tranche - et la résolution du mode déduit est itérative : elle ne
  //    retombe pas toujours pile sur le brut. L'écart constaté est de 5 F sur
  //    une opération d'un million. Minuscule, mais un livre qui ne boucle pas
  //    au franc près cesse d'inspirer confiance, et le résidu grandirait avec
  //    une grille plus grossière. On le comptabilise donc là où il naît, dans
  //    les frais opérateur, plutôt que de le laisser fausser le prix de
  //    revient du stock.
  if (r.feeMode === 'deduit') {
    const residu = r.provision - netEur * rateOut;
    if (Math.abs(residu) >= 0.5) {
      mouvements.push({
        type: 'frais_operateur',
        corridor,
        montantEur: 0,
        taux: 0,
        montantCfa: residu,      // négatif = nous décaissons moins que le brut
        parite,
      });
    }
  } else if (r.opTotal > 0) {
    mouvements.push({
      type: 'frais_operateur',
      corridor,
      // Refacturés, le client nous les rembourse en euros et l'opération est
      // neutre au centime d'arrondi près. À notre charge, la jambe euro
      // n'existe pas et la charge pèse en entier.
      montantEur: r.feeMode === 'refacture' ? r.opEurClient : 0,
      taux: 0,
      montantCfa: r.opTotal,
      parite,
    });
  }

  return mouvements;
}

/** Sens entrant : nous VENDONS des euros. */
function entrant(r, rateIn, corridor, parite) {
  const mouvements = [];

  // 1. L'échange, au taux appliqué. La recette est le net après frais de
  //    service, puisque c'est cette part qui est réellement convertie.
  mouvements.push({
    type: 'vente_eur',
    corridor,
    montantEur: r.recuEur,
    taux: rateIn,
    montantCfa: r.recuEur * rateIn,
  });

  // 2. Les frais de service, encaissés en CFA dans ce sens : ils sont déjà
  //    dans la bonne unité, aucune conversion.
  if (r.fraisCfa > 0) {
    mouvements.push({
      type: 'frais_service',
      corridor,
      montantEur: 0,
      taux: 0,
      montantCfa: r.fraisCfa,
    });
  }

  // 3. Asymétrie assumée avec le sens sortant : ici le client règle
  //    l'opérateur depuis son propre compte, les frais ne nous traversent pas.
  //    Ils ne deviennent un mouvement que si nous les absorbons.
  if (r.feeMode === 'acharge' && r.opTotal > 0) {
    mouvements.push({
      type: 'frais_operateur',
      corridor,
      montantEur: 0,
      taux: 0,
      montantCfa: r.opTotal,
      parite,
    });
  }

  return mouvements;
}

/**
 * Trésorerie attendue d'un devis, lue directement sur le résultat de cotation.
 *
 * Sert de contrôle indépendant : la somme des mouvements produits ci-dessus
 * doit donner exactement ces deux nombres. C'est l'invariant qui protège de
 * l'erreur la plus coûteuse - un devis qui déséquilibre le livre sans que rien
 * ne le signale.
 */
export function tresorerieAttendue(r) {
  if (r.direction === 'out') {
    return {
      // Ce que le client nous règle réellement en euros.
      eur: r.totalRegleEur,
      // Ce que nous décaissons au pays. Le moteur de cotation l'expose déjà
      // sous le nom « provision » : le net crédité au destinataire plus les
      // frais opérateur. La formule vaut dans les trois modes, y compris
      // « déduit » où le net est réduit d'autant, de sorte que la somme reste
      // le brut. J'avais d'abord traité « déduit » à part, ce qui donnait un
      // écart de 1 925 F : la distinction n'existe pas ici.
      cfa: -r.provision,
    };
  }
  return {
    eur: -r.recuEur,
    cfa: r.montantCollecte - (r.feeMode === 'acharge' ? r.opTotal : 0),
  };
}
